import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { UserPlus, UserCheck } from "lucide-react";
import { api, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/*
 * GiftAnimation
 * ==============
 * Full-screen celebration overlay used when a viewer sends OR replays a
 * coin-gift attached to a comment. Seven tiers of intensity ramp up as the
 * gifted amount grows. Sub-tier amounts (e.g. 75 coins) play the next-lower
 * tier's animation (50).
 *
 * Coin display value: 1 coin = $0.01 (matches internal wallet math). Split is
 * 60/40 (creator/platform) — the animation shows both the amount sent and
 * the creator's actual take.
 *
 * Prop API:
 *   gift = {
 *     coins_amount: number,
 *     gift_tier: 10|50|100|200|300|500|1000,
 *     tipper: {name, picture},
 *     text?: string,
 *   }
 *   onDone: () => void   (fired when the animation queue slot is finished)
 *   durationMs?: number  (defaults per tier)
 */

// --- Tier config ---------------------------------------------------------
// Each tier picks: emoji particles, colors, particle count, duration, and
// which macro-effect to layer on top (screen shake, radial shockwave, etc.).
export const TIERS = {
  10: {
    label: "SPARK",
    color: "#00FFFF",
    particles: ["💛"],
    particleCount: 18,
    duration: 2200,
    macro: "hearts",
    scale: 1,
  },
  50: {
    label: "HEART BURST",
    color: "#FF3E96",
    particles: ["💖", "💗"],
    particleCount: 40,
    duration: 2800,
    macro: "hearts",
    scale: 1.2,
  },
  100: {
    label: "ROSE STORM",
    color: "#FF007F",
    particles: ["🌹", "💐"],
    particleCount: 65,
    duration: 3200,
    macro: "roses",
    scale: 1.4,
  },
  200: {
    label: "FIREWORKS",
    color: "#FF9500",
    particles: ["✨", "🎆", "💥"],
    particleCount: 90,
    duration: 3800,
    macro: "fireworks",
    scale: 1.6,
  },
  300: {
    label: "GALAXY",
    color: "#B24CFF",
    particles: ["🌌", "⭐", "🌟", "💫"],
    particleCount: 120,
    duration: 4200,
    macro: "galaxy",
    scale: 1.8,
  },
  500: {
    label: "DRAGON",
    color: "#39FF14",
    particles: ["🐉", "🔥", "⚡"],
    particleCount: 150,
    duration: 4800,
    macro: "dragon",
    scale: 2.0,
  },
  1000: {
    label: "SUPERNOVA",
    color: "#FFD700",
    particles: ["🌞", "💥", "⚡", "✨", "🌟"],
    particleCount: 220,
    duration: 5500,
    macro: "supernova",
    scale: 2.4,
  },
};

const TIER_ORDER = [10, 50, 100, 200, 300, 500, 1000];

export function tierForCoins(coins) {
  if (!coins || coins <= 0) return null;
  let best = null;
  for (const t of TIER_ORDER) {
    if (coins >= t) best = t;
    else break;
  }
  return best;
}

// Random helpers — kept deterministic-ish across renders via useMemo.
function rng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

// --- The overlay ---------------------------------------------------------
export default function GiftAnimation({ gift, onDone, durationMs, onDismiss }) {
  const [visible, setVisible] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [isFollowing, setIsFollowing] = useState(
    Boolean(gift?.viewer_follows_tipper)
  );
  const [followBusy, setFollowBusy] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const tier = gift?.gift_tier || tierForCoins(gift?.coins_amount) || 10;
  const cfg = TIERS[tier] || TIERS[10];
  const duration = durationMs ?? cfg.duration;

  useEffect(() => {
    setIsFollowing(Boolean(gift?.viewer_follows_tipper));
  }, [gift?.viewer_follows_tipper, gift?.id]);

  const showFollowPill = Boolean(
    user &&
      gift?.tipper?.username &&
      !gift?.viewer_is_tipper &&
      user.username !== gift.tipper.username
  );

  const toggleFollow = async (e) => {
    e.stopPropagation();
    if (!user) {
      toast.error("Sign in to follow");
      return;
    }
    if (followBusy) return;
    setFollowBusy(true);
    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);
    try {
      if (wasFollowing) {
        await api.delete(`/users/${gift.tipper.username}/follow`);
      } else {
        await api.post(`/users/${gift.tipper.username}/follow`);
        toast.success(`Following @${gift.tipper.username}`);
      }
    } catch {
      setIsFollowing(wasFollowing); // revert
      toast.error("Could not update follow");
    } finally {
      setFollowBusy(false);
    }
  };

  useEffect(() => {
    // Reset dismiss flag when a new gift lands.
    setDismissed(false);
    setVisible(true);
  }, [gift?.id, gift?.tip_id]);

  useEffect(() => {
    if (!gift || dismissed) return;
    const t = setTimeout(() => {
      setVisible(false);
    }, duration - 350); // start fade slightly before end
    const t2 = setTimeout(() => {
      onDone?.();
    }, duration);
    return () => {
      clearTimeout(t);
      clearTimeout(t2);
    };
  }, [gift, duration, onDone, dismissed]);

  // Tap-to-dismiss: kill this animation immediately and let the parent
  // decide whether to advance the queue or clear it entirely.
  const dismiss = () => {
    if (dismissed) return;
    setDismissed(true);
    setVisible(false);
    // Small delay so the fade-out has time to render before the next frame
    // (300ms matches the AnimatePresence exit transition).
    setTimeout(() => {
      if (onDismiss) onDismiss();
      else onDone?.();
    }, 260);
  };

  // Tap on the tipper's avatar/name → open their profile. Also dismisses
  // the animation queue so the viewer lands on the profile immediately.
  const openTipperProfile = (e) => {
    e.stopPropagation();
    const uname = gift?.tipper?.username;
    if (!uname) return dismiss();
    setDismissed(true);
    setVisible(false);
    setTimeout(() => {
      if (onDismiss) onDismiss();
      else onDone?.();
      navigate(`/u/${uname}`);
    }, 220);
  };

  const seed = useMemo(() => {
    // stable per-gift seed so re-renders don't shuffle particles
    const s = (gift?.id || gift?.tip_id || String(gift?.coins_amount || 0)) + tier;
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return h || 1;
  }, [gift, tier]);

  // Coin values — use the app's REAL wallet math ($0.01/coin) so the on-screen
  // number matches what the creator actually earned. Split is 60/40 in the
  // creator's favor.
  const coinsSent = gift?.coins_amount || 0;
  const creatorCoins =
    typeof gift?.creator_coins === "number"
      ? gift.creator_coins
      : Math.floor((coinsSent * 60) / 100);
  const usdSent = (coinsSent * 0.01).toFixed(2);
  const usdCreator = (creatorCoins * 0.01).toFixed(2);

  const particles = useMemo(() => {
    const r = rng(seed);
    return Array.from({ length: cfg.particleCount }).map((_, i) => ({
      id: i,
      char: cfg.particles[Math.floor(r() * cfg.particles.length)],
      // start position — most from the bottom, some scattered
      startX: r() * 100,               // vw %
      startY: 100 + r() * 30,          // vh %, offscreen bottom
      driftX: (r() - 0.5) * 40,        // -20..20 vw
      endY: -20 - r() * 30,            // top
      size: 24 + r() * (cfg.scale * 36),
      rot: (r() - 0.5) * 720,          // total spin
      dur: duration * (0.7 + r() * 0.5) / 1000,
      delay: r() * 0.6,
    }));
  }, [cfg, duration, seed]);

  if (!gift) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="gift-animation"
          data-tier={tier}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          onClick={dismiss}
          onTouchStart={dismiss}
          role="button"
          aria-label="Dismiss gift animation"
          className="fixed inset-0 z-[80] cursor-pointer overflow-hidden"
          style={{
            // Radial glow behind everything — color tinted per tier
            background:
              tier >= 300
                ? `radial-gradient(circle at 50% 60%, ${cfg.color}22 0%, transparent 60%), radial-gradient(circle at 50% 60%, #00000060 30%, transparent 80%)`
                : `radial-gradient(circle at 50% 65%, ${cfg.color}18 0%, transparent 55%)`,
          }}
        >
          {/* Macro FX layer — screen shake, shockwave, dragon streak. */}
          <MacroFx tier={tier} cfg={cfg} duration={duration} />

          {/* Particles */}
          {particles.map((p) => (
            <motion.div
              key={p.id}
              initial={{
                left: `${p.startX}vw`,
                top: `${p.startY}vh`,
                rotate: 0,
                opacity: 0,
                scale: 0.6,
              }}
              animate={{
                left: `${p.startX + p.driftX}vw`,
                top: `${p.endY}vh`,
                rotate: p.rot,
                opacity: [0, 1, 1, 0.9, 0],
                scale: [0.6, 1, 1.05, 1, 0.9],
              }}
              transition={{
                duration: p.dur,
                delay: p.delay,
                ease: "easeOut",
                times: [0, 0.15, 0.5, 0.8, 1],
              }}
              className="absolute select-none"
              style={{
                fontSize: p.size,
                filter:
                  tier >= 200
                    ? `drop-shadow(0 0 8px ${cfg.color})`
                    : `drop-shadow(0 0 4px ${cfg.color}aa)`,
                willChange: "transform, opacity",
              }}
            >
              {p.char}
            </motion.div>
          ))}

          {/* Center reveal card — tipper + coin count. Tapping the avatar or
              name opens their profile; tapping elsewhere still dismisses. */}
          <motion.div
            initial={{ opacity: 0, scale: 0.4, y: 40 }}
            animate={{
              opacity: 1,
              scale: [0.4, 1.15, 1],
              y: 0,
            }}
            exit={{ opacity: 0, y: -20, scale: 0.8 }}
            transition={{ duration: 0.9, ease: [0.2, 1.2, 0.4, 1] }}
            className="absolute inset-x-0 top-[38%] flex flex-col items-center pointer-events-none px-6"
          >
            <button
              type="button"
              onClick={openTipperProfile}
              data-testid="gift-tipper-profile-btn"
              className="pointer-events-auto flex flex-col items-center focus:outline-none group"
              aria-label={`View ${gift?.tipper?.name || "tipper"}'s profile`}
            >
              {gift?.tipper?.picture && (
                <motion.img
                  src={picUrl(gift.tipper.picture)}
                  alt=""
                  className="h-16 w-16 object-cover rounded-full border-2 shadow-xl mb-3 group-hover:scale-110 transition-transform"
                  style={{ borderColor: cfg.color, boxShadow: `0 0 30px ${cfg.color}` }}
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              )}
            <p
              className="font-mono text-[10px] uppercase tracking-[0.35em] mb-1 opacity-90"
              style={{ color: cfg.color, textShadow: `0 0 12px ${cfg.color}` }}
            >
              // {cfg.label}
            </p>
            <p className="font-display font-black text-white text-3xl sm:text-5xl mb-1 text-center leading-none group-hover:underline">
              {gift?.tipper?.name || "@" + (gift?.tipper?.username || "anon")}
            </p>
            {gift?.tipper?.username && (
              <p
                className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/50 mt-0.5 group-hover:text-white transition-colors"
              >
                @{gift.tipper.username} · tap to view profile
              </p>
            )}
            </button>

            {showFollowPill && (
              <motion.button
                type="button"
                onClick={toggleFollow}
                disabled={followBusy}
                data-testid="gift-follow-pill"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.4 }}
                className={`pointer-events-auto mt-3 flex items-center gap-1.5 px-4 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-all disabled:opacity-50 ${
                  isFollowing
                    ? "border border-white/40 text-white/80 bg-black/40"
                    : "text-black font-bold shadow-lg"
                }`}
                style={
                  isFollowing
                    ? undefined
                    : { background: cfg.color, boxShadow: `0 0 24px ${cfg.color}` }
                }
              >
                {isFollowing ? (
                  <>
                    <UserCheck className="h-3 w-3" /> Following
                  </>
                ) : (
                  <>
                    <UserPlus className="h-3 w-3" /> Follow
                  </>
                )}
              </motion.button>
            )}

            <p
              className="font-display font-black text-5xl sm:text-7xl leading-none mt-3"
              style={{ color: cfg.color, textShadow: `0 0 20px ${cfg.color}` }}
            >
              +{coinsSent.toLocaleString()}
            </p>
            <p className="font-mono text-xs uppercase tracking-widest text-white/80 mt-1">
              coins sent · ${usdSent}
            </p>
            <p
              className="font-mono text-[11px] uppercase tracking-widest mt-2"
              style={{ color: cfg.color, opacity: 0.9 }}
              data-testid="gift-creator-take"
            >
              → {creatorCoins.toLocaleString()} coins ({`$${usdCreator}`}) to creator
            </p>
            {gift.text && (
              <p className="font-mono text-xs text-white/70 mt-4 max-w-md text-center line-clamp-2 italic">
                “{gift.text}”
              </p>
            )}
          </motion.div>

          {/* Bottom shine */}
          <motion.div
            className="absolute inset-x-0 bottom-0 h-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.6, 0] }}
            transition={{ duration: duration / 1000, ease: "easeInOut" }}
            style={{
              background: `linear-gradient(to top, ${cfg.color}44 0%, transparent 100%)`,
            }}
          />

          {/* Tap-to-dismiss hint — appears ~1s after the animation starts. */}
          <motion.p
            data-testid="gift-dismiss-hint"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 0.7, y: 0 }}
            transition={{ duration: 0.4, delay: 1 }}
            className="absolute left-1/2 -translate-x-1/2 bottom-6 font-mono text-[10px] uppercase tracking-[0.35em] text-white/70 pointer-events-none"
          >
            tap anywhere to dismiss
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// --- Per-tier macro effects ---------------------------------------------
function MacroFx({ tier, cfg, duration }) {
  // Radial shockwave (all tiers get one)
  const ring = (
    <motion.div
      key="ring"
      className="absolute left-1/2 top-1/2 rounded-full border-4"
      style={{
        translateX: "-50%",
        translateY: "-50%",
        borderColor: cfg.color,
        boxShadow: `0 0 40px ${cfg.color}`,
      }}
      initial={{ width: 0, height: 0, opacity: 0.9 }}
      animate={{ width: "160vmax", height: "160vmax", opacity: 0 }}
      transition={{ duration: Math.min(2.2, duration / 1000 * 0.6), ease: "easeOut" }}
    />
  );

  // Second bigger ring for 200+
  const ring2 =
    tier >= 200 ? (
      <motion.div
        key="ring2"
        className="absolute left-1/2 top-1/2 rounded-full border-2"
        style={{
          translateX: "-50%",
          translateY: "-50%",
          borderColor: "#fff",
        }}
        initial={{ width: 0, height: 0, opacity: 0.7 }}
        animate={{ width: "200vmax", height: "200vmax", opacity: 0 }}
        transition={{ duration: Math.min(2.6, duration / 1000 * 0.7), delay: 0.2, ease: "easeOut" }}
      />
    ) : null;

  // Screen-shake wrapper for 300+
  const shake =
    tier >= 300 ? (
      <motion.div
        key="shake"
        className="absolute inset-0"
        animate={{
          x: [0, -8, 8, -6, 6, -3, 3, 0],
          y: [0, 4, -4, 3, -3, 2, -2, 0],
        }}
        transition={{ duration: 0.8, repeat: tier >= 500 ? 3 : 1, ease: "easeInOut" }}
      />
    ) : null;

  // Rain of light streaks for 500+
  const streaks =
    tier >= 500
      ? Array.from({ length: 14 }).map((_, i) => {
          const left = 5 + (i * 90) / 14 + Math.random() * 3;
          return (
            <motion.div
              key={`s-${i}`}
              className="absolute w-[3px]"
              style={{
                left: `${left}%`,
                top: "-30%",
                height: "50vh",
                background: `linear-gradient(to bottom, transparent 0%, ${cfg.color} 40%, #fff 90%)`,
                filter: `drop-shadow(0 0 12px ${cfg.color})`,
              }}
              initial={{ y: "-30vh", opacity: 0 }}
              animate={{ y: "130vh", opacity: [0, 1, 0.9, 0] }}
              transition={{ duration: 1.1, delay: i * 0.04, ease: "easeIn" }}
            />
          );
        })
      : null;

  // Center flash for 1000
  const flash =
    tier >= 1000 ? (
      <motion.div
        key="flash"
        className="absolute inset-0 bg-white"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.95, 0.35, 0] }}
        transition={{ duration: 1.2, times: [0, 0.15, 0.35, 1], ease: "easeOut" }}
      />
    ) : null;

  return (
    <>
      {ring}
      {ring2}
      {shake}
      {streaks}
      {flash}
    </>
  );
}
